import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/opt/build/repo/src/components/layout.js";
import { Themed } from "theme-ui";
import About from "../content/about";
import Talks from "../content/talks";
import Quotes from "../content/quotes";
import GatesTweet from "../content/gatesTweet";
import CellxgeneSection from "../content/cellxgeneSection";
import PolisSection from "../content/polisSection";
import Prado from "../content/prado";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">






<p>{`I am deeply interested in problems of collective behavior and future models of open scientific collaboration.`}</p>
<p>{`My work since 2011 has focused on designing and building open source platforms which enable people to visualize and interactively explore high dimensional spaces.`}</p>
<p>{`I am `}<a parentName="p" {...{
        "href": "https://twitter.com/colinmegill"
      }}>{`@colinmegill`}</a>{` on Twitter, and open DMs are the best way to get in touch with me.`}</p>
<p>{`My bluesky is `}<a parentName="p" {...{
        "href": "https://staging.bsky.app/profile/colinmegill.com"
      }}>{`https://staging.bsky.app/profile/colinmegill.com`}</a></p>
<p>{`My mastodon is `}<a rel="me" href="https://mastodon.social/@colinmegill"><a parentName="p" {...{
          "href": "https://mastodon.social/@colinmegill"
        }}>{`https://mastodon.social/@colinmegill`}</a></a></p>
<p>{`I love backpacking, surfing and parenting 3 boys. I travel frequently and love seeing new places.`}</p>
<h1>{`Work`}</h1>
<h3>{`Polis`}</h3>
<p><a parentName="p" {...{
        "href": "https://compdemocracy.org/Polis/"
      }}>{`Polis`}</a>{` is a real-time system for gathering, analyzing and understanding what large groups of people think in their own words, enabled by advanced statistics and machine learning. Polis has been used all over the world by governments, academics, independent media and citizens, and is completely open source.`}</p>
<p>{`The development of novel methods for gathering voice, and the development of the platform `}<a parentName="p" {...{
        "href": "https://civichall.org/civicist/vtaiwan-democracy-frontier/"
      }}>{`were a response`}</a>{` to communication challenges within large scale decentralized movements evidenced in Occupy Wall Street and the Arab Spring.`}</p>
<p>{`Our startup (also called Polis after the technology) open sourced the technology at the 2016 g0v summit in Taiwan, and the organization transitioned to a 501c3 between 2018 and 2020. I transitioned from CEO of the startup to the board of the nonprofit, called The Computational Democracy Project, which stewards the codebase, the main deployment hosted at pol.is, and the mission and vision.`}</p>
<p>{`The AGPLv3 licensed codebase `}<a parentName="p" {...{
        "href": "https://github.com/compdemocracy/polis"
      }}>{`can be accessed on GitHub`}</a>{`.`}</p>
<PolisSection mdxType="PolisSection" />
<p>{`There are now multiple deployments of the technology, including at national scale by Taiwan's government and at city scale by the municipality of Amsterdam in the Netherlands.`}</p>
<p>{`Case studies and outcomes have been featured in `}<a parentName="p" {...{
        "href": "https://www.theatlantic.com/magazine/archive/2021/04/the-internet-doesnt-have-to-be-awful/618079/"
      }}>{`The Atlantic`}</a>{`, `}<a parentName="p" {...{
        "href": "https://www.nytimes.com/2019/10/15/opinion/taiwan-digital-democracy.html"
      }}>{`NYT`}</a>{`, `}<a parentName="p" {...{
        "href": "https://www.wired.co.uk/article/taiwan-democracy-social-media"
      }}>{`Wired`}</a>{`, `}<a parentName="p" {...{
        "href": "https://www.theguardian.com/world/2020/sep/27/taiwan-civic-hackers-polis-consensus-social-media-platform"
      }}>{`The Guardian`}</a>{`, `}<a parentName="p" {...{
        "href": "https://www.technologyreview.com/2018/08/21/240284/the-simple-but-ingenious-system-taiwan-uses-to-crowdsource-its-laws/"
      }}>{`MIT Tech Review`}</a>{` and `}<a parentName="p" {...{
        "href": "https://www.economist.com/open-future/2019/03/22/technology-and-political-will-can-create-better-governance"
      }}>{`The Economist`}</a>{` among others, and have been the subject of `}<a parentName="p" {...{
        "href": "https://www.youtube.com/embed/VbCZvU7i7VY"
      }}>{`a short BBC documentary`}</a>{` as well as featured in multiple books from major publishers.`}</p>
<blockquote className="twitter-tweet">
  <p lang="en" dir="ltr">
    The foundational paper for Polis (
    <a href="https://twitter.com/UsePolis?ref_src=twsrc%5Etfw">@UsePolis</a>) as
    a method has been released in a special edition of the journal RECERCA
    <br />
    <br />
    Huge thanks to
    <a href="https://twitter.com/cesifoti?ref_src=twsrc%5Etfw">@cesifoti</a>
    for advancing visibility+formalization of the democracy innovation space + for
    invaluable feedback in the production of the manuscript.
    <a href="https://t.co/ysws1VSctZ">https://t.co/ysws1VSctZ</a>
  </p>
  &mdash; Colin Megill (@colinmegill)
  <a href="https://twitter.com/colinmegill/status/1445044310722822147?ref_src=twsrc%5Etfw">
    October 4, 2021
  </a>
</blockquote>
<h3>{`Nextstrain`}</h3>
<p>{`From `}<a parentName="p" {...{
        "href": "nextstrain.org"
      }}>{`nextstrain.org`}</a>{`: "Nextstrain is an open-source project to harness the scientific and public health potential of pathogen genome data. We provide a continually-updated view of publicly available data alongside powerful analytic and visualization tools for use by the community. Our goal is to aid epidemiological understanding and improve outbreak response."`}</p>
<GatesTweet mdxType="GatesTweet" />
<p>{`My personal involvement with Nextstrain began with a weekend evening hack night project in 2014 to pull together real time statistics on the Ebola epidemic. This was very similar, in retrospect, to many of the hack projects that resulted in real time dashboards for COVID.`}</p>
<blockquote className="twitter-tweet tw-align-center">
  <p lang="en" dir="ltr">
    <a href="https://t.co/wCgy8M0hLt">https://t.co/wCgy8M0hLt</a> is an
    auto-updating visualization of{" "}
    <a href="https://twitter.com/hashtag/Ebola?src=hash&amp;ref_src=twsrc%5Etfw">
      #Ebola
    </a>{" "}
    case counts and mortality by{" "}
    <a href="https://twitter.com/colinmegill?ref_src=twsrc%5Etfw">
      @colinmegill
    </a>{" "}
    et al. <a href="http://t.co/sukqUZGIaS">pic.twitter.com/sukqUZGIaS</a>
  </p>
  &mdash; Trevor Bedford (@trvrb) <a href="https://twitter.com/trvrb/status/521711843341901824?ref_src=twsrc%5Etfw">October 13, 2014</a>
</blockquote>
<p>{`Between 2015 and 2017, I worked with `}<a parentName="p" {...{
        "href": "https://bedford.io/"
      }}>{`Bedford Lab`}</a>{` to platformitize, generalize and expand `}<a parentName="p" {...{
        "href": "https://twitter.com/trvrb/status/569907432161353728"
      }}>{`the nextflu prototype`}</a>{` to handle arbitrary viral phylogeny datasets.`}</p>
<blockquote className="twitter-tweet tw-align-center">
  <p lang="en" dir="ltr">
    Thanks to efforts from{" "}
    <a href="https://twitter.com/colinmegill?ref_src=twsrc%5Etfw">
      @colinmegill
    </a>
    , @jh_viz and <a href="https://twitter.com/richardneher?ref_src=twsrc%5Etfw">
      @richardneher
    </a>, we&#39;re relaunching <a href="https://t.co/WjdKmtoIuV">
      https://t.co/WjdKmtoIuV
    </a> today with tons of new features. <a href="https://t.co/0xKpjjH5s9">pic.twitter.com/0xKpjjH5s9</a>
  </p>
  &mdash; Trevor Bedford (@trvrb) <a href="https://twitter.com/trvrb/status/836608470463959042?ref_src=twsrc%5Etfw">February 28, 2017</a>
</blockquote>
<blockquote className="twitter-tweet tw-align-center">
  <p lang="en" dir="ltr">
    Thanks to{" "}
    <a href="https://twitter.com/colinmegill?ref_src=twsrc%5Etfw">
      @colinmegill
    </a>
    , <a href="https://t.co/WjdKmtGjmt">https://t.co/WjdKmtGjmt</a> now better links
    map and tree. We can show, for example, spread of Ebola across West Africa. <a href="https://t.co/ProTVjCwNH">pic.twitter.com/ProTVjCwNH</a>
  </p>
  &mdash; Trevor Bedford (@trvrb) <a href="https://twitter.com/trvrb/status/844289196940410881?ref_src=twsrc%5Etfw">March 21, 2017</a>
</blockquote>
<p>{`Nextstrain went on to win the Open Science Prize, and contribute to the surveillance of viruses like COVID. I was `}<a parentName="p" {...{
        "href": "https://twitter.com/colinmegill/status/1245249044647206912"
      }}>{`interviewed by Shirley Wu about the tool and how it's used to monitor virus evolution`}</a>{` in March 2020 at the outset of the coronavirus pandemic.`}</p>
<blockquote className="twitter-tweet tw-align-center">
  <p lang="en" dir="ltr">
    <a href="https://t.co/5iPXiYxiZC">https://t.co/5iPXiYxiZC</a> wins the
    OpenSciencePrize!{" "}
    <a href="https://t.co/T4bbmlgRIE">https://t.co/T4bbmlgRIE</a> great collab
    with <a href="https://twitter.com/trvrb?ref_src=twsrc%5Etfw">@trvrb</a>{" "}
    <a href="https://twitter.com/colinmegill?ref_src=twsrc%5Etfw">
      @colinmegill
    </a>{" "}
    @jh_viz
  </p>
  &mdash; Richard Neher (@richardneher) <a href="https://twitter.com/richardneher/status/836607453945081857?ref_src=twsrc%5Etfw">February 28, 2017</a>
</blockquote>
<blockquote className="twitter-tweet tw-align-center">
  <p lang="en" dir="ltr">
    Visualize the virus radiating out from China, mutating as it spreads.
    <br />
    <br />
    Right: map with different strains color-coded
    <br />
    Left: phylogenetic tree, showing how individual strains mutate and diverge
    <br />
    <br />
    Very impressive work by{" "}
    <a href="https://twitter.com/trvrb?ref_src=twsrc%5Etfw">@trvrb</a> and the
    group at Nextstrain.{" "}
    <a href="https://t.co/p3wkhvtDPL">https://t.co/p3wkhvtDPL</a>{" "}
    <a href="https://t.co/BuoxBaW81A">pic.twitter.com/BuoxBaW81A</a>
  </p>
  &mdash; balajis.com (@balajis) <a href="https://twitter.com/balajis/status/1233190944129929217?ref_src=twsrc%5Etfw">February 28, 2020</a>
</blockquote>
<p>{`The Nextstrain paper was published in `}<a parentName="p" {...{
        "href": "https://doi.org/10.1093/bioinformatics/bty407"
      }}>{`Bioinformatics, Volume 34, Issue 23, 01 December 2018, Pages 4121–4123`}</a>{`.`}</p>
<p>{`The AGPLv3 codebase `}<a parentName="p" {...{
        "href": "https://github.com/nextstrain/auspice"
      }}>{`is available on GitHub`}</a>{`.`}</p>
<p>{`Nextstrain has been featured in `}<a parentName="p" {...{
        "href": "https://www.nytimes.com/2020/04/22/us/coronavirus-sequencing.html"
      }}>{`NYT`}</a>{`, `}<a parentName="p" {...{
        "href": "https://www.technologyreview.com/2020/03/04/905512/gene-sleuths-are-tracking-the-coronavirus-outbreak-as-it-happens/"
      }}>{`MIT Tech Review`}</a>{`, `}<a parentName="p" {...{
        "href": "https://www.scientificamerican.com/article/virus-mutations-reveal-how-covid-19-really-spread1/"
      }}>{`Scientific American`}</a>{`, `}<a parentName="p" {...{
        "href": "https://www.vanityfair.com/news/2020/03/mapping-the-spread-of-a-deadly-disease"
      }}>{`Vanity Fair`}</a>{`, `}<a parentName="p" {...{
        "href": "https://www.bloomberg.com/news/articles/2020-03-24/how-did-covid-19-spread-viral-genetics-leave-trail-of-clues"
      }}>{`Bloomberg`}</a>{` and won a `}<a parentName="p" {...{
        "href": "https://winners.webbyawards.com/2020/specialachievement/270/nextstrainorg"
      }}>{`Webby`}</a>{`.`}</p>
<h3>{`cellxgene`}</h3>
<p>{`cellxgene is a highly general, MIT licensed tool for visualizing, exploring, and interactivley running computation on high dimensional data (that is, large, sparce matrices, their features, and their metadata).`}</p>
<p>{`cellxgene was built for the purpose of enabling pre-publication (benchtop) data annotation and post publication sharing of the many scRNAseq datasets emerging from The Human Cell Atlas, a project described at length in the 2018 MIT Tech Review article `}<a parentName="p" {...{
        "href": "https://www.technologyreview.com/2018/08/22/140786/the-cartographer-of-cells/"
      }}>{`The Cartographer of Cells`}</a>{`.`}</p>
<p>{`Additionally, cellxgene `}<a parentName="p" {...{
        "href": "https://www.covid19cellatlas.org/index.patient.html"
      }}>{`was used to facilate rapid dissemination of many single cell datasets related to COVID-19 illnesses`}</a>{`.`}</p>
<p>{`The paper, which describes the purpose, features, system architecture in depth, `}<a parentName="p" {...{
        "href": "https://www.biorxiv.org/content/10.1101/2021.04.05.438318v1.full"
      }}>{`is available on bioRxiv`}</a>{`.`}</p>
<p>{`The MIT licensed codebase `}<a parentName="p" {...{
        "href": "https://github.com/chanzuckerberg/cellxgene"
      }}>{`is available on GitHub`}</a>{`.`}</p>
<CellxgeneSection mdxType="CellxgeneSection" />
<p><em parentName="p">{`Above screenshot: four million cells interactively visualized from the Survey of Human Fetal Development.`}</em></p>
<p>{`See also: `}<a parentName="p" {...{
        "href": "https://www.economist.com/science-and-technology/2020/02/22/mapping-cells-to-build-a-complete-atlas-of-the-human-body"
      }}>{`Mapping Cells To Build a Complete Atlas of the Human Body`}</a>{` from The Economist.`}</p>
<h1>{`Talks`}</h1>
<Prado mdxType="Prado" />
<Talks mdxType="Talks" />
<h1>{`Quotes`}</h1>
<Quotes mdxType="Quotes" />

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      